import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";

// Context Providers
import { AuthProvider } from "./contexts/AuthContext";

// Layout Components
import AdminLayout from "./components/layout/AdminLayout";
import PublicLayout from "./components/layout/PublicLayout";
import AuthGuard from "./components/auth/AuthGuard";
import ImpersonationBanner from "./components/auth/ImpersonationBanner";

// Auth Pages
import LoginNew from "./pages/auth/LoginNew";
import Register from "./pages/auth/Register";
import ForgotPassword from "./pages/auth/ForgotPassword";

// Main Pages
import LandingPage from "./pages/LandingPage";
import Dashboard from "./pages/Dashboard";
import AdminDashboard from "./pages/admin/AdminDashboard";
import Users from "./pages/admin/Users";
import AdminProducts from "./pages/admin/Products";
import AdminSources from "./pages/admin/Sources";
import AdminOrders from "./pages/admin/Orders";
import AdminCustomers from "./pages/admin/Customers";
import AdminAnalytics from "./pages/admin/Analytics";
import AADEDashboard from "./pages/admin/AADEDashboard";
import AADEInsights from "./pages/admin/AADEInsights";
import AADEDecisions from "./pages/admin/AADEDecisions";
import Products from "./pages/Products";
import ProductDetail from "./pages/ProductDetail";
import Farms from "./pages/Farms";
import FarmDetail from "./pages/FarmDetail";
import Orders from "./pages/Orders";
import OrderDetail from "./pages/OrderDetail";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import Messages from "./pages/Messages";
import Notifications from "./pages/Notifications";
import PartnershipContractOnboarding from "./pages/onboarding/PartnershipContractOnboarding";

// PWA Components
import PWAPrompt from "./components/pwa/PWAPrompt";
import OfflineIndicator from "./components/common/OfflineIndicator";

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <ImpersonationBanner />
        <Routes>
          {/* Public Routes */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<LandingPage />} />
          </Route>
          {/* Auth Routes - redirect if already logged in */}
          <Route
            path="/auth/login"
            element={
              <AuthGuard requireAuth={false}>
                <LoginNew />
              </AuthGuard>
            }
          />
          <Route
            path="/auth/register"
            element={
              <AuthGuard requireAuth={false}>
                <Register />
              </AuthGuard>
            }
          />
          <Route
            path="/auth/forgot-password"
            element={
              <AuthGuard requireAuth={false}>
                <ForgotPassword />
              </AuthGuard>
            }
          />
          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <AuthGuard requiredCapability="capability:admin_console">
                <AdminLayout />
              </AuthGuard>
            }
          >
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="sources" element={<AdminSources />} />
            <Route path="products/:id" element={<ProductDetail />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="orders/:id" element={<OrderDetail />} />
            <Route path="users" element={<Users />} />
            <Route path="customers" element={<AdminCustomers />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="aade/dashboard" element={<AADEDashboard />} />
            <Route path="aade/insights" element={<AADEInsights />} />
            <Route path="aade/decisions" element={<AADEDecisions />} />
            <Route path="settings" element={<Settings />} />
          </Route>{" "}
          {/* Staff Routes */}
          <Route
            path="/staff"
            element={
              <AuthGuard requiredCapability="capability:ops_dashboard">
                <div>Staff Layout</div>
              </AuthGuard>
            }
          >
            <Route index element={<Navigate to="/staff/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="products" element={<Products />} />
            <Route path="products/:id" element={<ProductDetail />} />
            <Route path="orders" element={<Orders />} />
            <Route path="orders/:id" element={<OrderDetail />} />
            <Route path="profile" element={<Profile />} />
            <Route path="settings" element={<Settings />} />
          </Route>
          {/* Customer Routes */}
          <Route
            path="/dashboard"
            element={
              <AuthGuard requiredCapability="capability:customer_portal">
                <Dashboard />
              </AuthGuard>
            }
          />
          <Route
            path="/products"
            element={
              <AuthGuard requiredCapability="capability:catalog_access">
                <div>Customer Products Layout</div>
              </AuthGuard>
            }
          >
            <Route index element={<Products />} />
            <Route path=":id" element={<ProductDetail />} />
          </Route>
          <Route
            path="/farms"
            element={
              <AuthGuard requiredCapability="capability:catalog_access">
                <div>Customer Farms Layout</div>
              </AuthGuard>
            }
          >
            <Route index element={<Farms />} />
            <Route path=":id" element={<FarmDetail />} />
          </Route>
          <Route
            path="/orders"
            element={
              <AuthGuard requiredCapability="capability:ops_dashboard">
                <div>Customer Orders Layout</div>
              </AuthGuard>
            }
          >
            <Route index element={<Orders />} />
            <Route path=":id" element={<OrderDetail />} />
          </Route>
          <Route
            path="/profile"
            element={
              <AuthGuard>
                <Profile />
              </AuthGuard>
            }
          />{" "}
          {/* Protected Routes (any authenticated user) */}
          <Route
            path="/settings"
            element={
              <AuthGuard>
                <Settings />
              </AuthGuard>
            }
          />
          <Route
            path="/messages"
            element={
              <AuthGuard>
                <Messages />
              </AuthGuard>
            }
          />
          <Route
            path="/notifications"
            element={
              <AuthGuard>
                <Notifications />
              </AuthGuard>
            }
          />
          <Route
            path="/onboarding/partnership-contract"
            element={
              <AuthGuard>
                <PartnershipContractOnboarding />
              </AuthGuard>
            }
          />
          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>{" "}
        {/* Global Components */}
        <PWAPrompt />
        <OfflineIndicator />
      </div>
    </AuthProvider>
  );
}

export default App;
